import { NgForOf, NgIf } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { skip } from 'rxjs';
import { IFormAssociation, IOeeFormAssociation, ITypeBasedSelection } from 'scw-digital-boards-widgets';
import { IDropdownOption, MatDropdown, TDropdownId } from '../mat-dropdown/mat-dropdown.component';
import {
  EOeeFormAssociationEnum,
  TChildDropdowns,
  TConfig,
  TDropdownConfig,
  TFormAssociationEnum,
} from './form-association.interface';
import { FormAssociationStore } from './form-association.store';

@Component({
  imports: [MatDropdown, NgForOf, NgIf],
  providers: [FormAssociationStore],
  selector: 'app-form-association',
  standalone: true,
  templateUrl: './form-association.component.html',
})
export class FormAssociationComponent implements OnInit {
  @Input() formControl!: FormControl<IFormAssociation | null>;
  @Input() isFormSubmitted = false;

  public enumValues: TFormAssociationEnum[] = [
    ...(Object.values(EOeeFormAssociationEnum) as EOeeFormAssociationEnum[]),
  ];

  public formAssociation: TDropdownConfig<TFormAssociationEnum> = {
    configurationInput: this.store.getDefaultMultiSelectConfig({
      enableSearchFilter: false,
      isClientSide: true,
      singleSelection: false,
      text: 'formAssociation',
    }),
    formControl: new FormControl<IDropdownOption<TFormAssociationEnum>[] | null>([], {
      validators: [Validators.required],
    }),
    options: this.enumValues.map((key) => ({
      id: key,
      name: this.translate.instant(`widget.formIo.formAssociationOptions.${key}`),
    })),
  };

  public childDropdowns: TChildDropdowns<TDropdownId> = {} as TChildDropdowns<TDropdownId>;
  public selectedIdsByChildDropdown: Partial<Record<TFormAssociationEnum, TDropdownId[]>> = {};
  public parentSelectedOptions: IDropdownOption<TFormAssociationEnum>[] = [];
  private currentFieldSelectedTypeMap: Partial<Record<TFormAssociationEnum, string>> = {
    [EOeeFormAssociationEnum.currentWorkOrder]: 'lineId',
    [EOeeFormAssociationEnum.currentShift]: 'lineId',
  };

  constructor(
    private readonly translate: TranslateService,
    private readonly store: FormAssociationStore,
  ) {}

  public ngOnInit(): void {
    this.formAssociation.formControl.valueChanges
      .pipe(skip(1))
      .subscribe((selected: IDropdownOption<TFormAssociationEnum>[] | null) =>
        this.onFormAssociationSelectionChange(selected as IDropdownOption<TFormAssociationEnum>[] | null),
      );

    if (this.formControl.value?.oeeFormAssociation) {
      this.initializeFormAssociation(this.formControl.value.oeeFormAssociation);
    }
  }

  private initializeFormAssociation(oeeFormAssociation: IOeeFormAssociation): void {
    const selectedOptions: IDropdownOption<TFormAssociationEnum>[] = this.enumValues
      .filter((key: TFormAssociationEnum) => {
        const value: number[] | ITypeBasedSelection = oeeFormAssociation[key as keyof IOeeFormAssociation];

        if (!value) {
          return false;
        }

        if ('ids' in value) {
          return (value.ids ?? []).length > 0;
        }

        return (value as number[]).length > 0;
      })
      .map((key: TFormAssociationEnum) => {
        return this.formAssociation.options.find((opt: IDropdownOption<TFormAssociationEnum>) => opt.id === key)!;
      })
      .filter(Boolean);

    this.formAssociation.formControl.setValue(selectedOptions, { emitEvent: false });
  }

  private onFormAssociationSelectionChange(selected: IDropdownOption<TFormAssociationEnum>[] | null): void {
    const newSelected: IDropdownOption<TFormAssociationEnum>[] = selected || [];

    this.handleDeselectedFormAssociation(newSelected);

    newSelected.forEach((option) => {
      this.loadAndSetChildDropdownData(option.id);
    });

    this.parentSelectedOptions = newSelected;
  }

  private handleDeselectedFormAssociation(newSelected: IDropdownOption<TFormAssociationEnum>[]): void {
    const deselected: IDropdownOption<TFormAssociationEnum>[] = this.formAssociation.options.filter(
      (opt: IDropdownOption<TFormAssociationEnum>) =>
        !newSelected.some((n: IDropdownOption<TFormAssociationEnum>) => n.id === opt.id),
    );

    deselected.forEach((opt: IDropdownOption<TFormAssociationEnum>) => this.clearChildSelection(opt.id));
  }

  private onChildSelectionChange(
    optionId: TFormAssociationEnum,
    childDropdownSelection: IDropdownOption<TDropdownId>[] | null,
  ): void {
    this.selectedIdsByChildDropdown[optionId] =
      childDropdownSelection?.map((c: IDropdownOption<TDropdownId>) => c.id) || [];

    this.updateFormAssociation();
  }

  private clearChildSelection(optionId: TFormAssociationEnum): void {
    this.selectedIdsByChildDropdown[optionId] = [];

    if (this.childDropdowns[optionId]?.formControl) {
      this.childDropdowns[optionId].formControl.setValue([]);
    }

    if (this.childDropdowns[optionId]) {
      delete this.childDropdowns[optionId];
    }
  }

  private loadAndSetChildDropdownData(optionId: TFormAssociationEnum): void {
    const config: TConfig | undefined = this.getConfigForOption(optionId);

    if (!config) {
      return;
    }

    this.loadAndSubscribe(optionId, config);
  }

  private getConfigForOption(optionId: TFormAssociationEnum): TConfig | undefined {
    const configMap: Partial<Record<TFormAssociationEnum, TConfig>> = {
      [EOeeFormAssociationEnum.shift]: {
        configurationInput: () => this.store.getDefaultMultiSelectConfig({ text: 'shift' }),
        getSelectedIds: (oee: IOeeFormAssociation) => oee.shift ?? [],
        loadFn: (searchText?: string) => {
          this.store.loadShift({
            fields: ['name'],
            limit: 1000,
            ...(searchText ? { search: { searchedFields: ['name'], searchText } } : {}),
          });
        },
        observable$: this.store.shiftsDropdown$,
      },
      [EOeeFormAssociationEnum.line]: {
        configurationInput: () => this.store.getDefaultMultiSelectConfig({ text: 'line' }),
        getSelectedIds: (oee: IOeeFormAssociation) => oee.line ?? [],
        loadFn: (searchText?: string) =>
          this.store.loadLine({
            fields: ['title'],
            limit: 1000,
            ...(searchText ? { search: { searchedFields: ['title'], searchText } } : {}),
          }),
        observable$: this.store.linesDropdown$,
      },
      [EOeeFormAssociationEnum.currentShift]: {
        configurationInput: () =>
          this.store.getDefaultMultiSelectConfig({ hint: 'currentShiftHint', singleSelection: true, text: 'line' }),
        getSelectedIds: (oee: IOeeFormAssociation) => oee.currentShift?.ids ?? [],
        loadFn: (searchText?: string) =>
          this.store.loadLine({
            fields: ['title'],
            limit: 1000,
            ...(searchText ? { search: { searchedFields: ['title'], searchText } } : {}),
          }),
        observable$: this.store.linesDropdown$,
      },
      [EOeeFormAssociationEnum.currentWorkOrder]: {
        configurationInput: () =>
          this.store.getDefaultMultiSelectConfig({ hint: 'currentWorkOrderHint', singleSelection: true, text: 'line' }),
        getSelectedIds: (oee: IOeeFormAssociation) => oee.currentWorkOrder?.ids ?? [],
        loadFn: (searchText?: string) =>
          this.store.loadLine({
            fields: ['title'],
            limit: 1000,
            ...(searchText ? { search: { searchedFields: ['title'], searchText } } : {}),
          }),
        observable$: this.store.linesDropdown$,
      },
      [EOeeFormAssociationEnum.workOrder]: {
        configurationInput: () => this.store.getDefaultMultiSelectConfig({ text: 'workOrder' }),
        getSelectedIds: (oee: IOeeFormAssociation) => oee.workOrder ?? [],
        loadFn: (searchText?: string) =>
          this.store.loadWorkOrder({
            fields: ['woNumber'],
            limit: 1000,
            ...(searchText ? { search: { searchedFields: ['woNumber'], searchText } } : {}),
          }),
        observable$: this.store.workOrdersDropdown$,
      },
    };

    return configMap[optionId];
  }

  private loadAndSubscribe(optionId: TFormAssociationEnum, config: TConfig | undefined): void {
    if (!config) {
      return;
    }

    const oeeFormAssociation: IOeeFormAssociation | undefined = this.formControl.value?.oeeFormAssociation;

    const formControl: FormControl<IDropdownOption<TDropdownId>[] | null> = this.createChildFormControl(optionId);
    this.initializeChildDropdown(optionId, config, formControl);
    this.subscribeToOptions(optionId, config, formControl, oeeFormAssociation);

    config.loadFn?.();
  }

  private createChildFormControl(optionId: TFormAssociationEnum): FormControl<IDropdownOption<TDropdownId>[] | null> {
    const formControl = new FormControl<IDropdownOption<TDropdownId>[] | null>([], {
      validators: [Validators.required],
    });

    formControl.valueChanges.subscribe((childSelected) => {
      this.onChildSelectionChange(optionId, childSelected);
    });

    return formControl;
  }

  private initializeChildDropdown(
    optionId: TFormAssociationEnum,
    config: TConfig,
    formControl: FormControl<IDropdownOption<TDropdownId>[] | null>,
  ): void {
    this.childDropdowns[optionId] = {
      configurationInput: config.configurationInput(),
      currentSearchText: '',
      formControl,
      hasLoadedInitialSelection: false,
      loadFn: (searchText?: string): void => {
        this.childDropdowns[optionId]!.currentSearchText = searchText ?? '';
        config.loadFn?.(searchText);
      },
      options: [],
    };
  }

  private subscribeToOptions(
    optionId: TFormAssociationEnum,
    config: TConfig,
    formControl: FormControl<IDropdownOption<TDropdownId>[] | null>,
    oeeFormAssociation: IOeeFormAssociation | undefined,
  ): void {
    config.observable$?.subscribe((options) => {
      const dropdown: TDropdownConfig<string | number | boolean> = this.childDropdowns[optionId]!;
      const searchText: string = dropdown?.currentSearchText ?? '';
      const selectedValues: IDropdownOption<TDropdownId>[] = formControl.value ?? [];

      dropdown.options = [
        ...options,
        ...(searchText ? [] : selectedValues.filter((v) => !options.some((opt) => String(opt.id) === String(v.id)))),
      ];

      this.loadInitialSelectionIfNeeded(dropdown, config, formControl, oeeFormAssociation);
    });
  }

  private loadInitialSelectionIfNeeded(
    dropdown: (typeof this.childDropdowns)[TFormAssociationEnum],
    config: TConfig,
    formControl: FormControl<IDropdownOption<TDropdownId>[] | null>,
    oeeFormAssociation: IOeeFormAssociation | undefined,
  ): void {
    if (
      dropdown?.hasLoadedInitialSelection ||
      !oeeFormAssociation ||
      !config.getSelectedIds ||
      !dropdown?.options.length
    ) {
      return;
    }

    const selectedIds: TDropdownId[] = config.getSelectedIds(oeeFormAssociation).map((id) => id);
    const finalSelection = dropdown.options.filter((opt) => selectedIds.includes(opt.id));

    formControl.setValue(finalSelection, { emitEvent: true });
    dropdown.hasLoadedInitialSelection = true;
  }

  private updateFormAssociation(): void {
    if (this.hasIncompleteSelection()) {
      this.formControl.setErrors({ incomplete: true });

      return;
    }

    this.formControl.setErrors(null);
    this.formControl.setValue({ oeeFormAssociation: this.buildOeeResult() as IOeeFormAssociation });
  }

  private getSelectedFormAssociation(): IDropdownOption<TFormAssociationEnum>[] {
    const selected: IDropdownOption<TFormAssociationEnum>[] = this.formAssociation.formControl.value ?? [];

    this.enumValues.forEach((key: TFormAssociationEnum) => {
      if (!selected.some((s: IDropdownOption<TFormAssociationEnum>) => s.id === key)) {
        this.selectedIdsByChildDropdown[key] = [];
      }
    });

    return selected;
  }

  private buildOeeResult(): Record<TFormAssociationEnum, ITypeBasedSelection | number[]> {
    const result: Record<TFormAssociationEnum, ITypeBasedSelection | number[]> = {} as Record<
      TFormAssociationEnum,
      ITypeBasedSelection | number[]
    >;

    this.enumValues.forEach((key: TFormAssociationEnum) => {
      const value: TDropdownId[] = this.selectedIdsByChildDropdown[key] ?? [];

      if (this.isTypeBasedSelection(key)) {
        result[key] = this.buildTypeBasedSelection(key, value);
      } else {
        result[key] = value as number[];
      }
    });

    return result;
  }

  private isTypeBasedSelection(key: TFormAssociationEnum): boolean {
    return [EOeeFormAssociationEnum.currentWorkOrder, EOeeFormAssociationEnum.currentShift].includes(
      key as EOeeFormAssociationEnum,
    );
  }

  private buildTypeBasedSelection(key: TFormAssociationEnum, value: TDropdownId[]): ITypeBasedSelection {
    return {
      ids: value as number[],
      selectedType: this.currentFieldSelectedTypeMap[key as EOeeFormAssociationEnum]!,
    };
  }

  private hasIncompleteSelection(): boolean {
    const selectedFormAssociation: IDropdownOption<TFormAssociationEnum>[] = this.getSelectedFormAssociation();

    return selectedFormAssociation.length === 0 || !this.areAllChildrenFilled(selectedFormAssociation);
  }

  private areAllChildrenFilled(selected: IDropdownOption<TFormAssociationEnum>[]): boolean {
    return selected.every(
      (option: IDropdownOption<TFormAssociationEnum>) => (this.selectedIdsByChildDropdown[option.id] ?? []).length > 0,
    );
  }
}
